import { PayloadAction } from '@reduxjs/toolkit';
import { WritableDraft } from 'immer';

import { QuoteBuilderStateType } from '../../types/state';
import { QuotePriceMarkupFieldOption } from '../../types/state/quote-price';
import { getQuotePriceOrThrow } from '../utilities/action-guards';
import { buildElementId } from '../utilities/element-id';
import { buildIssueId } from '../utilities/issue-id';
import { deleteIssue, upsertIssue } from '../utilities/issue-managers';
import { transformStringToDecimal } from '../utilities/transform-string-to-decimal';
import { QuotePriceParams } from './types';

interface Params extends QuotePriceParams {
  markupField: QuotePriceMarkupFieldOption;
  value: string;
}

const markupFieldNameMap: Record<QuotePriceMarkupFieldOption, string> = {
  burdenCostAmount: 'Burden Cost',
  burdenCostPercent: 'Burden Cost Percent',
  laborCostAmount: 'Labor Cost',
  laborCostPercent: 'Labor Cost Percent',
  purchaseMaterialAmount: 'Material Cost',
  purchaseMaterialPercent: 'Material Cost Percent',
  serviceCostAmount: 'Service Cost',
  serviceCostPercent: 'Service Cost Percent',
  fabricatedMaterialAmount: 'Fabricated Material',
  fabricatedMaterialPercent: 'Fabricated Material Percent',
};

export function markupFieldChanged(
  state: WritableDraft<QuoteBuilderStateType>,
  action: PayloadAction<Params>,
) {
  const { quoteLineUuid, quotePriceUuid, markupField, value } = action.payload;

  const quotePrice = getQuotePriceOrThrow(state, quoteLineUuid, quotePriceUuid);

  const issueId = buildIssueId({
    area: 'quotePrice',
    quoteLineUuid,
    quotePriceUuid,
    field: markupField,
    type: 'invalid',
  });

  const newValue = transformStringToDecimal(value, {
    customErrorMessages: {
      EMPTY: `${markupFieldNameMap[markupField]} is required`,
      NOT_A_POSITIVE_NUMBER: `${markupFieldNameMap[markupField]} must be greater than 0`,
      NOT_A_NUMBER: `${markupFieldNameMap[markupField]} must be a number`,
      INVALID: `${markupFieldNameMap[markupField]} must be a number`,
    },
  });

  if (!newValue.success) {
    upsertIssue(state, issueId, {
      title: `Invalid ${markupFieldNameMap[markupField]}`,
      description: newValue.error,
      type: 'invalid',
      severity: 'error',
      area: 'quotePrice',
      quoteLineUuid,
      quotePriceUuid,
      action: {
        type: 'modify',
        elementId: buildElementId({
          uuid: quotePriceUuid,
          field: markupField,
        }),
      },
    });

    return;
  }

  deleteIssue(state, issueId);

  quotePrice[markupField] = newValue.value;

  // @TODO: Update unit price and other derived values
}
